/**
 * Client-side Router for Dynamic Pages
 * Handles slug-based routing with SEO-friendly URLs
 */

import { Storage } from '../db/storage.js';

export class Router {
  #routes = new Map();
  #currentRoute = null;
  #abortController = new AbortController();
  #onRouteChange = null;

  constructor(onRouteChange) {
    this.#onRouteChange = onRouteChange;
    this.handlePopState = this.handlePopState.bind(this);
    window.addEventListener('popstate', this.handlePopState);
  }

  register(path, handler) {
    this.#routes.set(path, handler);
  }

  navigate(path, state = {}) {
    history.pushState(state, '', path);
    this.handleRoute(path);
  }

  handleRoute(path) {
    // Check for dynamic page route
    let slug = path.replace(/^\//, '').split('/')[0];
    if (slug) {
      try { slug = decodeURIComponent(slug); } catch(e) {}
    }
    
    if (!slug || slug === '') {
      this.#onRouteChange?.('home');
      return;
    }

    // Check if it's a dynamic page
    const page = Storage.getPage(slug);
    if (page) {
      this.#onRouteChange?.('page', slug, page);
      return;
    }

    // Check navbar items
    const navItems = Storage.getNavbarItems();
    const navItem = navItems.find(i => i.slug === slug);
    
    if (navItem) {
      if (navItem.type === 'internal' && !Storage.getPage(slug)) {
        // Create page on first visit if it doesn't exist
        this.createDefaultPage(slug, navItem);
        const newPage = Storage.getPage(slug);
        this.#onRouteChange?.('page', slug, newPage);
        return;
      }
      
      if (navItem.type === 'external') {
        window.open(navItem.url, navItem.target || '_blank');
        return;
      }
      
      if (navItem.type === 'section') {
        this.#onRouteChange?.('home', null, null, navItem.url);
        return;
      }
    }

    // 404
    this.#onRouteChange?.('404', slug);
  }

  createDefaultPage(slug, navItem) {
    const defaultPage = {
      slug,
      title: navItem.title,
      metaDescription: `${navItem.title} — Daily Network`,
      hero: {
        enabled: true,
        title: navItem.title,
        subtitle: '',
        image: '',
        overlayOpacity: 50,
        alignment: 'center'
      },
      blocks: [
        {
          id: `block-${Date.now()}`,
          type: 'text',
          content: {
            html: `<p>This is the ${navItem.title} page. Content can be edited via the admin panel.</p>`,
            align: 'center',
            maxWidth: 'medium'
          },
          settings: {},
          sortOrder: 1,
          isActive: true
        }
      ],
      isPublished: true,
      createdAt: new Date().toISOString()
    };
    Storage.savePage(slug, defaultPage);
  }

  handlePopState(e) {
    const path = window.location.pathname;
    this.handleRoute(path);
  }

  goToHome() {
    this.navigate('/');
  }

  goToPage(slug) {
    this.navigate(`/${slug}`);
  }

  destroy() {
    this.#abortController.abort();
    window.removeEventListener('popstate', this.handlePopState);
  }
}
